import { useEffect, useRef } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import styles from './BrazilMapGL.module.css';
import { getMapColors } from '../../../utils/mapColors';

const RAW_TOKEN = import.meta.env.VITE_MAPBOX_TOKEN as string | undefined;
const TOKEN = RAW_TOKEN?.startsWith('pk.') ? RAW_TOKEN : undefined;

const BRAZIL_BOUNDS: mapboxgl.LngLatBoundsLike = [
	[-73.99, -33.75],
	[-28.84, 5.27],
];

const STATES_SOURCE_ID = 'brazil-states';
const STATES_FILL_LAYER = 'states-fill';
const STATES_HOVER_LAYER = 'states-hover';
const STATES_LINE_LAYER = 'states-line';
const STATES_LABEL_LAYER = 'states-label';

const GEOJSON_URL =
	'https://raw.githubusercontent.com/codeforgermany/click_that_hood/main/public/data/brazil-states.geojson';

function buildMatchExpr(selected: string[], base: string, sel: string): mapboxgl.Expression {
	if (selected.length === 0) return ['literal', base];
	const expr: unknown[] = ['match', ['get', 'sigla']];
	selected.forEach((s) => expr.push(s, sel));
	expr.push(base);
	return expr as mapboxgl.Expression;
}

function buildLabelColorExpr(
	selected: string[],
	onSel: string,
	onBase: string
): mapboxgl.Expression {
	if (selected.length === 0) return ['literal', onBase];
	const expr: unknown[] = ['match', ['get', 'sigla']];
	selected.forEach((s) => expr.push(s, onSel));
	expr.push(onBase);
	return expr as mapboxgl.Expression;
}

function buildLabelHaloExpr(
	selected: string[],
	haloSel: string,
	haloBase: string
): mapboxgl.Expression {
	if (selected.length === 0) return ['literal', haloBase];
	const expr: unknown[] = ['match', ['get', 'sigla']];
	selected.forEach((s) => expr.push(s, haloSel));
	expr.push(haloBase);
	return expr as mapboxgl.Expression;
}

interface BrazilMapGLProps {
	selectedStateCodes: string[];
	interactive?: boolean;
	showLabels?: boolean;
	onToggleState?: (code: string) => void;
	className?: string;
}

export const BrazilMapGL = ({
	selectedStateCodes,
	interactive = false,
	showLabels = false,
	onToggleState,
	className,
}: BrazilMapGLProps) => {
	const containerRef = useRef<HTMLDivElement>(null);
	const mapRef = useRef<mapboxgl.Map | null>(null);
	const loadedRef = useRef(false);
	const selectedRef = useRef(selectedStateCodes);
	selectedRef.current = selectedStateCodes;

	useEffect(() => {
		if (!containerRef.current || !TOKEN) return;

		mapboxgl.accessToken = TOKEN;

		const colors = getMapColors();

		const map = new mapboxgl.Map({
			container: containerRef.current,
			style: 'mapbox://styles/mapbox/empty-v9',
			bounds: BRAZIL_BOUNDS,
			fitBoundsOptions: { padding: 6, duration: 0 },
			interactive,
			attributionControl: false,
			logoPosition: 'bottom-right',
		});

		mapRef.current = map;

		const resizeObserver = new ResizeObserver(() => map.resize());
		resizeObserver.observe(containerRef.current);

		map.on('load', () => {
			loadedRef.current = true;

			map.addSource(STATES_SOURCE_ID, {
				type: 'geojson',
				data: GEOJSON_URL,
				generateId: true,
			});

			map.addLayer({
				id: STATES_FILL_LAYER,
				type: 'fill',
				source: STATES_SOURCE_ID,
				paint: {
					'fill-color': buildMatchExpr(selectedRef.current, colors.base, colors.selected),
					'fill-opacity': 0.9,
				},
			});

			map.addLayer({
				id: STATES_LINE_LAYER,
				type: 'line',
				source: STATES_SOURCE_ID,
				paint: {
					'line-color': colors.border,
					'line-width': ['interpolate', ['linear'], ['zoom'], 3, 0.5, 6, 1.2],
				},
			});

			if (showLabels) {
				map.addLayer({
					id: STATES_LABEL_LAYER,
					type: 'symbol',
					source: STATES_SOURCE_ID,
					layout: {
						'text-field': ['get', 'sigla'],
						'text-font': ['DIN Offc Pro Bold', 'Arial Unicode MS Bold'],
						'text-size': ['interpolate', ['linear'], ['zoom'], 3, 9, 5, 12, 7, 15],
						'text-allow-overlap': false,
						'text-ignore-placement': false,
						'text-letter-spacing': 0.08,
					},
					paint: {
						'text-color': buildLabelColorExpr(
							selectedRef.current,
							colors.labelOnSelected,
							colors.labelOnBase
						),
						'text-halo-color': buildLabelHaloExpr(
							selectedRef.current,
							colors.labelHaloSelected,
							colors.labelHaloBase
						),
						'text-halo-width': 1.5,
					},
				});
			}

			if (interactive && onToggleState) {
				map.addLayer({
					id: STATES_HOVER_LAYER,
					type: 'fill',
					source: STATES_SOURCE_ID,
					paint: {
						'fill-color': colors.hover,
						'fill-opacity': ['case', ['boolean', ['feature-state', 'hovered'], false], 0.25, 0],
					},
				});

				let hoveredId: number | null = null;

				map.on('mousemove', STATES_FILL_LAYER, (e) => {
					map.getCanvas().style.cursor = 'pointer';
					const id = e.features?.[0]?.id as number | undefined;
					if (id === undefined) return;
					if (hoveredId !== null && hoveredId !== id) {
						map.setFeatureState({ source: STATES_SOURCE_ID, id: hoveredId }, { hovered: false });
					}
					hoveredId = id;
					map.setFeatureState({ source: STATES_SOURCE_ID, id }, { hovered: true });
				});

				map.on('mouseleave', STATES_FILL_LAYER, () => {
					map.getCanvas().style.cursor = '';
					if (hoveredId !== null) {
						map.setFeatureState({ source: STATES_SOURCE_ID, id: hoveredId }, { hovered: false });
						hoveredId = null;
					}
				});

				map.on('click', STATES_FILL_LAYER, (e) => {
					const sigla = e.features?.[0]?.properties?.sigla as string | undefined;
					if (sigla) onToggleState(sigla);
				});
			}
		});

		return () => {
			loadedRef.current = false;
			resizeObserver.disconnect();
			map.remove();
			mapRef.current = null;
		};
	}, [interactive, showLabels]);

	useEffect(() => {
		const map = mapRef.current;
		if (!map || !loadedRef.current) return;
		if (!map.getLayer(STATES_FILL_LAYER)) return;
		const colors = getMapColors();
		map.setPaintProperty(
			STATES_FILL_LAYER,
			'fill-color',
			buildMatchExpr(selectedStateCodes, colors.base, colors.selected)
		);
		if (showLabels && map.getLayer(STATES_LABEL_LAYER)) {
			map.setPaintProperty(
				STATES_LABEL_LAYER,
				'text-color',
				buildLabelColorExpr(selectedStateCodes, colors.labelOnSelected, colors.labelOnBase)
			);
			map.setPaintProperty(
				STATES_LABEL_LAYER,
				'text-halo-color',
				buildLabelHaloExpr(selectedStateCodes, colors.labelHaloSelected, colors.labelHaloBase)
			);
		}
	}, [selectedStateCodes, showLabels]);

	useEffect(() => {
		const applyThemeColors = () => {
			const map = mapRef.current;
			if (!map || !loadedRef.current) return;
			const colors = getMapColors();
			if (map.getLayer(STATES_FILL_LAYER)) {
				map.setPaintProperty(
					STATES_FILL_LAYER,
					'fill-color',
					buildMatchExpr(selectedRef.current, colors.base, colors.selected)
				);
			}
			if (map.getLayer(STATES_LINE_LAYER)) {
				map.setPaintProperty(STATES_LINE_LAYER, 'line-color', colors.border);
			}
			if (map.getLayer(STATES_HOVER_LAYER)) {
				map.setPaintProperty(STATES_HOVER_LAYER, 'fill-color', colors.hover);
			}
			if (showLabels && map.getLayer(STATES_LABEL_LAYER)) {
				map.setPaintProperty(
					STATES_LABEL_LAYER,
					'text-color',
					buildLabelColorExpr(selectedRef.current, colors.labelOnSelected, colors.labelOnBase)
				);
				map.setPaintProperty(
					STATES_LABEL_LAYER,
					'text-halo-color',
					buildLabelHaloExpr(selectedRef.current, colors.labelHaloSelected, colors.labelHaloBase)
				);
			}
		};

		const observer = new MutationObserver(applyThemeColors);
		observer.observe(document.documentElement, {
			attributes: true,
			attributeFilter: ['data-mode'],
		});
		return () => observer.disconnect();
	}, [showLabels]);

	if (!TOKEN) {
		return;
	}

	return <div ref={containerRef} className={`${styles.map} ${className ?? ''}`} />;
};
